import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { projectsAPI, analyticsAPI } from '../utils/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const loadProject = useCallback(async () => {
    try {
      const response = await projectsAPI.getOne(id);
      setProject(response.data);
      
      // Track page view
      try {
        await analyticsAPI.trackPageView({
          project_id: response.data.id,
          project_title: response.data.title,
          page_key: 'project_detail',
          page_title: `Project: ${response.data.title}`,
          event_type: 'project_view',
          tab_name: '',
          user_agent: navigator.userAgent,
          ip_address: '' // IP will be detected on backend if needed
        });
      } catch (error) {
        console.error('Analytics tracking failed:', error);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const openLightbox = useCallback((index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    if (project && project.images) {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  }, [project]);

  const prevImage = useCallback(() => {
    if (project && project.images) {
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  }, [project]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <p className="text-muted-foreground">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="project-not-found">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Project not found</p>
          <Link to="/work" className="text-primary hover:underline" data-testid="back-to-work-link">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  const projectImages = project.images || [];
  const projectTools = project.tools || [];

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24" data-testid="project-detail-page">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-12"
            data-testid="back-to-work-button"
          >
            <ArrowLeft size={20} />
            Back to Work
          </Link>

          <div className="mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-mono text-primary mb-4" data-testid="project-category">
              {project.category}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold font-['Syne'] tracking-tight mb-6" data-testid="project-title">
              {project.title}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground" data-testid="project-description">
              {project.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-6xl mx-auto" data-testid="project-images">
            {projectImages.length > 0 && projectImages.map((image, index) => (
              <div 
                key={`img-${index}`} 
                className="aspect-video overflow-hidden border border-white/5 cursor-pointer group relative"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image}
                  alt={`${project.title} - ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                    Click to view full size
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Lightbox */}
          <AnimatePresence>
            {lightboxOpen && project && project.images && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                onClick={closeLightbox}
              >
                {/* Close Button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-50 text-white hover:text-primary transition-colors p-2"
                  aria-label="Close"
                >
                  <X size={32} />
                </button>

                {/* Image Counter */}
                <div className="absolute top-4 left-4 z-50 text-white text-sm font-medium">
                  {currentImageIndex + 1} / {project.images.length}
                </div>

                {/* Previous Button */}
                {project.images.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:text-primary transition-colors p-2"
                    aria-label="Previous"
                  >
                    <ChevronLeft size={48} />
                  </button>
                )}

                {/* Image */}
                <motion.img
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  src={project.images[currentImageIndex]}
                  alt={`${project.title} - ${currentImageIndex + 1}`}
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />

                {/* Next Button */}
                {project.images.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:text-primary transition-colors p-2"
                    aria-label="Next"
                  >
                    <ChevronRight size={48} />
                  </button>
                )}

                {/* Thumbnails */}
                {project.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 max-w-[90vw] overflow-x-auto p-2">
                    {project.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(idx);
                        }}
                        className={`flex-shrink-0 w-16 h-16 border-2 transition-all ${
                          idx === currentImageIndex
                            ? 'border-primary scale-110'
                            : 'border-white/30 hover:border-white/60'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
            {projectTools.length > 0 && (
              <div data-testid="project-tools">
                <h3 className="text-xl font-semibold font-['Syne'] mb-4">Tools Used</h3>
                <div className="flex flex-wrap gap-2">
                  {projectTools.map((tool, index) => (
                    <span
                      key={`tool-${index}`}
                      className="px-4 py-2 bg-secondary border border-white/10 rounded-full text-sm"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {project.approach && (
              <div data-testid="project-approach">
                <h3 className="text-xl font-semibold font-['Syne'] mb-4">Design Approach</h3>
                <p className="text-muted-foreground">{project.approach}</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;
